import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO component for managing meta tags across pages
 * Uses react-helmet-async for SSR-compatible meta tag management
 */
export default function SEO({
  title = 'IQ Reps - Cognitive Training',
  description = 'Advanced n-back cognitive training with 24 dimensions. Research-backed brain training to improve working memory and fluid intelligence.',
  keywords = 'n-back, cognitive training, brain training, working memory, IQ improvement, fluid intelligence',
  author = 'IQ Reps',
  ogType = 'website',
  ogImage = '/og-image.png',
  twitterCard = 'summary_large_image',
  canonicalUrl,
  noindex = false,
}) {
  // Build full URL for Open Graph
  const siteUrl = 'https://multinback.com'; // Update with your actual domain
  const fullUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  const imageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={fullUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="IQ Reps" />
      
      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
    </Helmet>
  );
}

/**
 * Preset configurations for common pages
 */
export const SEO_PRESETS = {
  landing: {
    title: 'IQ Reps - Double Your Working Memory in 20 Sessions',
    description: 'The most advanced n-back cognitive trainer with 24 dimensions. Train your brain in 15 minutes daily with research-backed methods. Start your free trial today.',
    keywords: 'n-back training, working memory, cognitive training, brain training, IQ improvement, dual n-back, fluid intelligence',
    canonicalUrl: '/',
  },
  
  pricing: {
    title: 'Pricing - IQ Reps Training Plans',
    description: 'Simple, transparent pricing for cognitive training. Start free with 12 rounds daily, or upgrade to Pro for unlimited access to all 24 dimensions. 30-day money-back guarantee.',
    keywords: 'n-back pricing, brain training cost, cognitive training subscription',
    canonicalUrl: '/pricing',
  },
  
  login: {
    title: 'Login - IQ Reps',
    description: 'Sign in to your IQ Reps account to continue your cognitive training.',
    noindex: true,
  },
  
  signup: {
    title: 'Sign Up - IQ Reps',
    description: 'Create your free IQ Reps account and start your cognitive training journey today.',
    noindex: true,
  },
  
  game: {
    title: 'Training - IQ Reps',
    description: 'Start your n-back training session. Improve your working memory with our advanced cognitive trainer.',
    noindex: true,
  },
  
  articles: {
    title: 'Articles - IQ Reps',
    description: 'Learn about n-back training, cognitive science, and working memory improvement with our research-backed articles.',
    keywords: 'n-back research, cognitive science, working memory articles',
    canonicalUrl: '/articles',
  },
};

